import {gql} from '@apollo/client';
import {css} from 'aphrodite';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {TASK_STATUS_ICON_STYLES, TASK_STATUS_ICONS} from 'processes/status/TaskStatusIcon';
import {sortTaskStatuses} from 'processes/status/TaskStatusUtil';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {AppConfigurationProcessTaskStatusSelectorQuery} from './__generated/AppConfigurationProcessTaskStatusSelectorQuery';

type ProcessTaskStatus = AppConfigurationProcessTaskStatusSelectorQuery['processTaskStatuses'][number];

export const APP_CONFIGURATION_PROCESS_TASK_STATUS_SELECTOR_QUERY = gql`
  query AppConfigurationProcessTaskStatusSelectorQuery @skipAudit @engComponent(name: BENCHLING_APPS) {
    processTaskStatuses {
      ... on ProcessTaskStatusNode {
        id
        displayName
        statusType
      }
    }
  }
`;
interface BaseProps {
  processTaskStatuses: Array<ProcessTaskStatus>;
  disabled: boolean;
  selectedTaskStatusId: string | null;
  onChange: (newSelectedStatusId: string | null) => void;
}

export function AppConfigurationProcessTaskStatusSelector(props: BaseProps): JSX.Element {
  const optionDatas: Array<OptionData> = [
    {
      name: 'No Task Status',
      value: null,
    },
    {
      name: 'Statuses',
      isHeader: true,
    },
    ...sortTaskStatuses(props.processTaskStatuses).map(({id, displayName, statusType}) => ({
      name: displayName,
      icon: TASK_STATUS_ICONS[statusType].icon,
      iconClassName: css(TASK_STATUS_ICON_STYLES[statusType]),
      value: id,
    })),
  ];
  const selectedTaskStatusDisplayName = props.processTaskStatuses.find(
    (taskStatus) => taskStatus.id === props.selectedTaskStatusId
  )?.displayName;

  return (
    <SelectDropdown
      menu={<SearchableMenu width={Menu.WIDTHS.FULL} optionDatas={optionDatas} onSelect={props.onChange} />}
      displayName={selectedTaskStatusDisplayName || 'Select a Task Status'}
      disabled={props.disabled}
    />
  );
}

export default function QueryingAppConfigurationProcessTaskStatusSelector(
  props: Omit<BaseProps, 'processTaskStatuses'>
): JSX.Element {
  const result = useBenchlingQuery<AppConfigurationProcessTaskStatusSelectorQuery>(
    APP_CONFIGURATION_PROCESS_TASK_STATUS_SELECTOR_QUERY
  );
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Unable to query workflow task statuses. Try refreshing the page.');
    return <div />;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    return (
      <AppConfigurationProcessTaskStatusSelector
        {...props}
        processTaskStatuses={result.data.processTaskStatuses}
      />
    );
  }
}
